import {SWIPER_ARGS_VIEW} from 'Includes/config';
import merge from 'lodash/merge';

export default class Slider {

    static _libPromise = null;

    static init() {
        const allSliderElements = Array.from(document.querySelectorAll('.wpbs-slider.swiper'));

        const slaves = allSliderElements.filter(el => el.classList.contains('--slave'));
        const masters = allSliderElements.filter(el => el.classList.contains('--master'));
        const others = allSliderElements.filter(el => !el.classList.contains('--slave') && !el.classList.contains('--master'));

        [...slaves, ...others, ...masters].forEach(element => {
            const swiperArgs = this.getRawContext(element);
            this.observe(element, swiperArgs);
        });
    }

    static getRawContext(element) {
        const configAttr = element.dataset?.context;
        const config = JSON.parse(configAttr);
        if (configAttr) {
            try {
                return config?.swiper;
            } catch (e) {
                console.error('Error parsing Swiper config JSON from data-context:', e);
            }
        }
        return {};
    }

    static async fetchYouTubeVideos(config) {

        const API_KEY = window.WPBS?.settings?.places?.maps_key ?? window.WPBS?.places?.maps_key ?? null;
        const CHANNEL_ID = config?.channel_id;

        if (!API_KEY || !CHANNEL_ID) return Promise.resolve([]);

        const url = `https://www.googleapis.com/youtube/v3/search?key=${API_KEY}&channelId=${CHANNEL_ID}&part=snippet,id&order=date&maxResults=${config?.page_size || 10}&type=video`;

        try {
            const response = await fetch(url);
            const data = await response.json();

            return Promise.resolve((data.items || []).map(v => ({
                ...(v?.snippet ?? {}),
                link: `https://youtube.com/watch?v=${v.id.videoId}`
            })));
        } catch (e) {
            console.error("API Error:", e);
            return [];
        }
    }

    static async initYoutube(element) {

        const enabled = element?.classList.contains('is-style-youtube');

        if (!enabled) {
            return false;
        }

        const config = JSON.parse(element.dataset?.context ?? '{}')?.youtube ?? {};
        const feed = await this.fetchYouTubeVideos(config);

        if (!feed?.length) {
            return false;
        }

        const wrapper = element.querySelector(':scope > .swiper-wrapper');
        const templateEl = wrapper.querySelector(':scope > template');

        if (!templateEl) {
            return false;
        }

        const getYoutubeVideoId = (item) => {
            if (item?.link) {
                const url = new URL(item.link);
                const vid = url.searchParams.get('v');
                if (vid) return vid;
            }

            const thumb = item?.thumbnails?.high?.url;
            if (thumb) {
                const match = thumb.match(/\/vi\/([^/]+)\//);
                if (match) return match[1];
            }

            return null;
        };

        const fragment = document.createDocumentFragment();

        feed.forEach(item => {
            const slide = templateEl.content.firstElementChild.cloneNode(true);

            const imageEl = slide.querySelectorAll('.--field-image');
            const titleEl = slide.querySelectorAll('.--field-title');
            const dateEl = slide.querySelectorAll('.--field-date');
            const linkEl = slide.querySelectorAll('.--link');

            const videoId = getYoutubeVideoId(item);

            if (videoId) {
                slide
                    .querySelectorAll('[data-vid]')
                    .forEach(el => el.dataset.vid = videoId);
            }

            linkEl.forEach(el => el.href = item.link ?? '');

            if (imageEl && item?.thumbnails?.high?.url) {
                imageEl.forEach(el => {
                    const img = document.createElement('img');
                    img.src = item.thumbnails.high.url;
                    img.alt = item.title || '';
                    el.replaceChildren(img);
                })
            }

            titleEl.forEach(el => el.textContent = item.title || '');

            dateEl.forEach(el => {
                const date = new Date(item.publishedAt);
                el.textContent = date.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                });
            });

            fragment.appendChild(slide);
        });

        templateEl.remove();

        wrapper.appendChild(fragment);
    }

    static mergeArgs(element, args = {}) {
        const merged = merge({}, SWIPER_ARGS_VIEW);
        const isMaster = element.classList.contains('--master');
        const isSlave = element.classList.contains('--slave');

        merged.navigation = merge({}, merged.navigation, args.navigation, {
            nextEl: element.querySelector('.wpbs-slider-button--next'),
            prevEl: element.querySelector('.wpbs-slider-button--prev'),
            addIcons: false,
        });

        if (typeof args.pagination === 'object' && args.pagination !== null) {
            merged.pagination = merge({}, merged.pagination, args.pagination);
        } else if (args.pagination === false) {
            merged.pagination = false;
        }


        Object.keys(args).forEach(key => {
            if (key !== 'navigation' && key !== 'pagination') {
                merged[key] = args[key];
            }
        });

        const controllerId = element.getAttribute('data-slider-controller');

        const controllerBy = controllerId ? (isMaster ? 'container' : 'slide') : (args.controller?.by || 'slide');

        merged.controller = merge({}, merged.controller, {
            by: controllerBy
        });

        if (isSlave) {
            merged.touchRatio = 0;
            merged.navigation = false;
            merged.pagination = false;

            delete merged.allowTouchMove;
            delete merged.keyboard;
        }

        return merged;
    }

    static linkMasterGroup(masterElement, masterInstance, controllerId) {
        const allLinkedElements = document.querySelectorAll(
            `.wpbs-slider.swiper[data-slider-controller="${controllerId}"]`
        );

        const allLinkedInstances = [];

        allLinkedElements.forEach(linkedEl => {
            let instance;
            if (!linkedEl.classList.contains('swiper-initialized')) {
                const linkedArgs = this.mergeArgs(linkedEl, this.getRawContext(linkedEl));
                instance = new Swiper(linkedEl, linkedArgs);
                linkedEl.swiper = instance;
                instance.update();
            } else {
                instance = linkedEl.swiper;
            }

            if (instance) {
                allLinkedInstances.push(instance);
            }
        });

        if (allLinkedInstances.length > 1) {
            allLinkedInstances.forEach(currentInstance => {
                currentInstance.controller.control = allLinkedInstances.filter(
                    targetInstance => targetInstance !== currentInstance
                );
                currentInstance.update();
            });
        }
    }

    static observe(element, args = {}) {
        if (element.classList.contains('swiper-initialized')) return;

        const mergedArgs = Object.fromEntries(
            Object.entries(this.mergeArgs(element, args)).filter(
                ([, value]) => value !== undefined && value !== null && value !== ""
            )
        );

        const controllerId = element.getAttribute('data-slider-controller');
        const isMaster = element.classList.contains('--master');
        const isSlave = element.classList.contains('--slave');
        const isYoutube = element.classList.contains('is-style-youtube');

        if (element.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide').length <= 1 && !isYoutube) return;

        const initFn = () => {
            const swiperInstance = new Swiper(element, mergedArgs);
            element.swiper = swiperInstance;
            swiperInstance.update();

            if (isMaster && controllerId) {
                this.linkMasterGroup(element, swiperInstance, controllerId);
            }
        };


        if (isSlave) {
            this.initLib(element).then(initFn).catch(console.error);
        } else {
            const observer = new IntersectionObserver((entries, observerInstance) => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) return;
                    observerInstance.unobserve(element);
                    this.initLib(element).then(initFn).catch(console.error);
                });
            }, {
                root: null,
                rootMargin: '90px',
                threshold: 0,
            });
            observer.observe(element);
        }
    }

    static async initLib(element) {
        if (this._libPromise) return this._libPromise;

        await this.initYoutube(element);

        if (typeof window.Swiper === 'function') {
            this._libPromise = Promise.resolve();
        } else {
            // Load CSS
            const link = document.createElement('link');
            Object.assign(link, {
                id: 'wpbs-swiper-css',
                rel: 'stylesheet',
                href: 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.css'
            });
            document.head.appendChild(link);

            // Load JS
            this._libPromise = new Promise((resolve, reject) => {
                const script = document.createElement('script');
                Object.assign(script, {
                    id: 'wpbs-swiper-js',
                    src: 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.js',
                    defer: true,
                    async: true,
                    onload: resolve,
                    onerror: reject
                });
                document.body.appendChild(script);
            });
        }
        return this._libPromise;
    }
}